import * as React from 'react';

import { computeText } from '@/locales/utils';
import { Input } from '@/components/form/Input';
import { FormattedMessage, useIntl } from 'react-intl';
import { Text } from '@/components/atoms/Text';
import { SvyButton } from '@/components/savory/SvyButton';
import { confirm } from '@/core/asyncActions/confirm';
import { useChangeOrderDateInReportMutation } from '@/services/innovorder/adminTools/adminTools.endpoints';
import { FormControl, FormControls, SuccessMessage } from '../AdminTools.style';
import { safeMutate } from '@/utils/errors';
import { Form } from '@/core/form/Form';

export type ChangeOrderDateInReportInputs = {
    orderPosId: string;
    day: string;
    month: string;
    year: string;
    hour: string;
    minutes: string;
};

function isValidDateParts({ day, month, year, hour, minutes }: ChangeOrderDateInReportInputs): {
    ok: boolean;
    error?: string;
} {
    const y = Number(year);
    const m = Number(month);
    const d = Number(day);
    const H = Number(hour);
    const M = Number(minutes);

    if (![y, m, d, H, M].every((n) => Number.isFinite(n))) {
        return { ok: false, error: `Valeurs invalides : ${day}/${month}/${year} ${hour}:${minutes}` };
    }
    if (m < 1 || m > 12) return { ok: false, error: `Mois invalide : ${month}` };
    if (d < 1 || d > 31) return { ok: false, error: `Jour invalide : ${day}` };
    if (H < 0 || H > 23) return { ok: false, error: `Heure invalide : ${hour}` };
    if (M < 0 || M > 59) return { ok: false, error: `Minutes invalides : ${minutes}` };

    const dt = new Date(y, m - 1, d, H, M, 0, 0);
    const same =
        dt.getFullYear() === y &&
        dt.getMonth() === m - 1 &&
        dt.getDate() === d &&
        dt.getHours() === H &&
        dt.getMinutes() === M;

    if (!same) {
        const HH = hour.toString().padStart(2, '0');
        const MM = minutes.toString().padStart(2, '0');
        return { ok: false, error: `La date ${day}/${month}/${year} ${HH}:${MM} est invalide.` };
    }

    return { ok: true };
}

const ChangeOrderDateInReport: React.FunctionComponent = () => {
    const intl = useIntl();
    const [changeOrderDateInReport, { isSuccess }] = useChangeOrderDateInReportMutation();

    const [formError, setFormError] = React.useState<string | null>(null);

    const handleSubmitForm = async (inputs: ChangeOrderDateInReportInputs) => {
        setFormError(null);

        const validity = isValidDateParts(inputs);
        if (!validity.ok) {
            setFormError(validity.error || 'Date invalide.');
            return;
        }

        const hasConfirmed = await confirm(computeText(intl, 'adminTools.confirmModal.description'));
        if (hasConfirmed) {
            await safeMutate(
                changeOrderDateInReport({
                    orderPosId: inputs.orderPosId,
                    day: inputs.day,
                    month: inputs.month,
                    year: inputs.year,
                    hour: inputs.hour,
                    minutes: inputs.minutes,
                }),
            );
        }
    };

    return (
        <Form<ChangeOrderDateInReportInputs>>
            {({ handleSubmit }) => (
                <>
                    <Text type="large" text="brand.adminTools.changeOrderDateInReport.title" />
                    <FormControls>
                        <FormControl>
                            <Input
                                name="orderPosId"
                                rules={{ required: computeText(intl, 'field.required') }}
                                placeholder="brand.adminTools.changeOrderDateInReport.form.placeholder.orderPosId"
                                labelId="brand.adminTools.changeOrderDateInReport.form.orderPosId"
                            />
                            <Input
                                name="day"
                                rules={{ required: computeText(intl, 'field.required') }}
                                placeholder="brand.adminTools.changeOrderDateInReport.form.placeholder.day"
                                labelId="brand.adminTools.changeOrderDateInReport.form.day"
                            />
                            <Input
                                name="month"
                                rules={{ required: computeText(intl, 'field.required') }}
                                placeholder="brand.adminTools.changeOrderDateInReport.form.placeholder.month"
                                labelId="brand.adminTools.changeOrderDateInReport.form.month"
                            />
                            <Input
                                name="year"
                                rules={{ required: computeText(intl, 'field.required') }}
                                placeholder="brand.adminTools.changeOrderDateInReport.form.placeholder.year"
                                labelId="brand.adminTools.changeOrderDateInReport.form.year"
                            />
                            <Input
                                name="hour"
                                rules={{ required: computeText(intl, 'field.required') }}
                                placeholder="brand.adminTools.changeOrderDateInReport.form.placeholder.hour"
                                labelId="brand.adminTools.changeOrderDateInReport.form.hour"
                            />
                            <Input
                                name="minutes"
                                rules={{ required: computeText(intl, 'field.required') }}
                                placeholder="brand.adminTools.changeOrderDateInReport.form.placeholder.minutes"
                                labelId="brand.adminTools.changeOrderDateInReport.form.minutes"
                            />
                        </FormControl>

                        {/* Error message */}
                        {formError && <div style={{ color: 'red', marginBottom: '8px' }}>{formError}</div>}

                        <SvyButton onClick={handleSubmit(handleSubmitForm)}>
                            <FormattedMessage id="button.validate" />
                        </SvyButton>

                        {isSuccess && <SuccessMessage text="brand.adminTools.changeOrderDateInReport.form.success" />}
                    </FormControls>
                </>
            )}
        </Form>
    );
};

export default ChangeOrderDateInReport;
